/**
 * Today's records (core todayrecords.ts; the audit's "is it working?" trail): what was written down today, newest first,
 * one line each (time · what · result · who). Tapping a row opens the record under it, with a link to where it lives
 * (the room's check, the reports, the food list). Managers see everyone's rows and the binder link; staff their own.
 * Glanceable (S15): the newest five, then "Show all N". Every save message links here ("See it in today's records").
 */
import { useEffect, useRef, useState } from "react";
import { MARK_TEXT_MAX, showReadings, showTemps, type RecordMarkView, type TempUnit } from "@logwave/core";
import { useTempUnit } from "../lib/units.ts";
import { Link, useLocation } from "react-router-dom";
import type { TodayRecordRow } from "@logwave/core";
import { MAX_PHOTO_BYTES, fileToBase64 } from "../lib/files.ts";
import { saveErrorText } from "../lib/failures.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useLiveRefresh } from "../lib/useLive.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, ErrorBox, Field, LinkButton, Pill, Section, Sheet, Skeleton, useToast } from "./ui.tsx";

/** Where "See it in today's records" goes. */
export const RECORDS_PATH = "/#records";
/** The newest rows shown before "Show all". */
export const RECORDS_SHOWN = 5;
const TABLES = ["log_entries", "incidents", "daily_checkins"] as const;

/** A record's own screen, when it has one. */
export function openPath(o: NonNullable<TodayRecordRow["open"]>): string {
  switch (o.to) {
    case "room": return `/rooms/check/${encodeURIComponent(o.id)}`;
    case "reports": return "/report?view=list";
    case "food": return "/food";
    case "fix": return `/do/fix/${encodeURIComponent(o.id)}`;
    case "clean": return "/clean";
  }
}
const OPEN_LABEL: Record<NonNullable<TodayRecordRow["open"]>["to"], string> = { room: "Open the room check", reports: "Open the reports", food: "Open the food list", fix: "Add what was done", clean: "Open the clean-up" };

export function TodayRecordsSection() {
  const api = useApi();
  const s = useSession();
  const loc = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const data = useLoad(() => api.todayRecords(s.org.id, s.location.id), [api, s.org.id, s.location.id]);
  useLiveRefresh(() => data.reload(), TABLES);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string>();
  // Readings in the viewer's unit (core temperature.ts T5); "entered as 6°C" stays as typed.
  const [unit] = useTempUnit();
  // Arrived from a save message ("See it in today's records"): scroll here.
  useEffect(() => { if (loc.hash === "#records" && data.data) ref.current?.scrollIntoView?.({ block: "start" }); }, [loc.hash, data.data]);
  const d = data.data;
  const rows = d ? (all ? d.rows : d.rows.slice(0, RECORDS_SHOWN)) : [];
  return (
    <div id="records" ref={ref}>
      <Section title="Today's records" kicker={d?.scope === "mine" ? "What you wrote down" : "What was written down"}>
        {!d ? (data.error ? <ErrorBox error={`We couldn't load today's records. ${data.error}`} onRetry={data.reload} /> : <Skeleton rows={2} label="Loading today's records" />) : (
          <div className="stack-sm" data-testid="today-records">
            <p className="small" role="status">{d.line}</p>
            {rows.length > 0 && (
              <ul className="plain records" aria-label="Today's records">
                {rows.map((r) => (
                  <li key={r.id} className={`record-row${r.tone ? ` tone-${r.tone}` : ""}`}>
                    <button type="button" className="linklike record-line" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? undefined : r.id)}>
                      <span className="num">{r.time}</span> <strong>{r.title}</strong>{r.line ? <> · {showReadings(r.line, unit)}</> : null}{r.who ? <> · {r.who}</> : null}
                    </button>
                    {r.flagged && <Pill tone="warn" icon="alert">Asked to look again</Pill>}
                    {open === r.id && (
                      <div className="record-detail stack-sm stage-in">
                        <dl className="plain">{r.detail.map((x, i) => <div key={i} className="row"><dt className="muted">{x.label}</dt><dd>{detailIn(x, unit)}</dd></div>)}</dl>
                        <RecordMarks marks={r.marks ?? []} />
                        {r.open && <Link to={openPath(r.open)}>{OPEN_LABEL[r.open.to]}</Link>}
                        <RecordActions row={r} onDone={() => data.reload()} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="row">
              {d.rows.length > RECORDS_SHOWN && <button type="button" className="linklike" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${d.rows.length}`}</button>}
              {d.binder && <LinkButton small variant="secondary" icon="download" to="/prove">Open the inspection binder</LinkButton>}
              {d.rows.some((r) => r.tone === "bad") && <Pill tone="stop" icon="alert">Needs a fix</Pill>}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

/* ───────── notes, "done wrong" and photo proof (core recordmarks.ts M1-M5) ───────── */

const MARK_ICON: Record<RecordMarkView["kind"], "paper" | "alert" | "camera"> = { note: "paper", wrong: "alert", photo: "camera" };
const MARK_LABEL: Record<RecordMarkView["kind"], string> = { note: "Note", wrong: "Done wrong", photo: "Photo" };

/** What was added under a record, oldest first. Nothing here can be edited or removed (M1). */
function RecordMarks({ marks }: { marks: RecordMarkView[] }) {
  // Marks that were there when the record opened stay still; one that arrives while it is open slides in and flashes once (audit fix 11).
  const seen = useRef<Set<string>>(new Set(marks.map((m) => m.id)));
  if (!marks.length) return null; // always mounted with the open record, so a first mark that arrives later counts as new
  return (
    <ul className="plain record-marks" aria-label="Added to this record">
      {marks.map((m) => (
        <li key={m.id} className={`record-mark mark-${m.kind}${seen.current.has(m.id) ? "" : " mark-new"}`}>
          <Icon name={MARK_ICON[m.kind]} size={16} />
          <span><strong>{MARK_LABEL[m.kind]}</strong> · {m.byName}, {m.time}{m.text ? <>: {m.text}</> : null}</span>
          {m.photoPath && <MarkPhoto path={m.photoPath} by={m.byName} />}
        </li>
      ))}
    </ul>
  );
}

function MarkPhoto({ path, by }: { path: string; by: string }) {
  const api = useApi();
  const s = useSession();
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (typeof URL === "undefined" || !URL.createObjectURL) { setFailed(true); return; }
    let live = true, made: string | undefined;
    api.recordPhoto(s.org.id, path).then((b) => { if (live) { made = URL.createObjectURL(b); setUrl(made); } }).catch(() => { if (live) { setUrl(undefined); setFailed(true); } });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [api, s.org.id, path]);
  // The square is reserved while the photo loads, so the page does not jump when it arrives (audit H7).
  if (url) return <img className="record-photo" src={url} alt={`Photo added by ${by}`} />;
  return failed ? null : <span className="record-photo" aria-hidden="true" data-testid="record-photo-wait" />;
}

type Adding = "note" | "wrong" | "photo";

/** The quiet actions under an open record. Only what this person may do (row.can); a check-in or sick report has none. */
function RecordActions({ row, onDone }: { row: TodayRecordRow; onDone: () => void }) {
  const [adding, setAdding] = useState<Adding>();
  if (!row.can.note && !row.can.photo && !row.can.wrong) return null;
  return (
    <>
      <div className="btn-row record-actions">
        {row.can.note && <Button small variant="quiet" icon="paper" aria-haspopup="dialog" onClick={() => setAdding("note")}>Add a note</Button>}
        {row.can.photo && <Button small variant="quiet" icon="camera" aria-haspopup="dialog" onClick={() => setAdding("photo")}>Add a photo</Button>}
        {row.can.wrong && <Button small variant="quiet" icon="alert" aria-haspopup="dialog" onClick={() => setAdding("wrong")}>This was done wrong</Button>}
      </div>
      {adding && <AddMarkSheet row={row} kind={adding} onClose={() => setAdding(undefined)} onDone={onDone} />}
    </>
  );
}

function AddMarkSheet({ row, kind, onClose, onDone }: { row: TodayRecordRow; kind: Adding; onClose: () => void; onDone: () => void }) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const title = kind === "note" ? "Add a note" : kind === "photo" ? "Add a photo" : "This was done wrong";
  async function submit() {
    if (kind !== "photo" && !text.trim()) { setErr(kind === "note" ? "Write your note first." : "Say what was wrong, so they know what to look at again."); return; }
    if (kind === "photo" && !photo) { setErr("Take or choose a photo first."); return; }
    if (photo && photo.size > MAX_PHOTO_BYTES) { setErr("That photo is too big."); return; }
    setBusy(true); setErr(undefined);
    try {
      const r = await api.markRecord(s.org.id, { locationId: s.location.id, recordRef: row.id, kind, ...(text.trim() ? { text: text.trim() } : {}), ...(photo ? { photoBase64: await fileToBase64(photo) } : {}) });
      toast.show(kind === "note" ? "Note added." : kind === "photo" ? "Photo added." : r.sent ? `Sent back. ${row.who ?? "They"} will see "Please look again".` : "Marked. It's on the record.", { icon: kind === "wrong" ? "alert" : "check" });
      onDone();
      onClose();
    } catch (e) { setErr(saveErrorText(e)); } finally { setBusy(false); }
  }
  return (
    <Sheet title={title} kicker={`${row.time} ${row.title}`} onClose={onClose}>
      {kind === "wrong" && <p className="muted">{row.who ?? "They"} gets a private "Please look again" with your words. The record stays as written, with your note under it.</p>}
      {kind === "note" && <p className="muted">It goes under the record. The record itself stays as written.</p>}
      {kind === "photo" && <p className="muted">A picture kept with the record as proof.</p>}
      {kind === "photo"
        ? <Field label="Photo">{(id) => <input id={id} type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0])} />}</Field>
        : <Field label={kind === "note" ? "Your note" : "What was wrong?"} hint={`${text.length} of ${MARK_TEXT_MAX}`}>{(id) => <textarea id={id} rows={3} maxLength={MARK_TEXT_MAX} value={text} onChange={(e) => setText(e.target.value)} placeholder={kind === "wrong" ? "The shelves were only wiped on the front" : "Milk delivery came late"} />}</Field>}
      {err && <ErrorBox error={err} />}
      <div className="btn-row">
        <Button busy={busy} icon={kind === "photo" ? "camera" : "check"} onClick={() => void submit()}>{kind === "wrong" ? "Send it back" : kind === "photo" ? "Add the photo" : "Add the note"}</Button>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
      </div>
    </Sheet>
  );
}

/** A record detail in the viewer's unit: a rule line ("Problem") as a limit, every other temperature as a reading. */
export function detailIn(x: { label: string; value: string }, unit: TempUnit): string {
  return x.label === "Problem" ? showTemps(x.value, unit) : showReadings(x.value, unit);
}
